import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { formatDateOnly } from '@/lib/dates/date-only';
import type { RecurringConfig } from '@/types/job';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth'];

const FREQUENCY_NOUN: Record<string, string> = {
  DAILY: 'day',
  WEEKLY: 'week',
  BIWEEKLY: '2 weeks',
  MONTHLY: 'month',
  QUARTERLY: 'quarter',
  SEMIANNUAL: '6 months',
  ANNUAL: 'year',
  CUSTOM: 'period',
};

function fmtDate(iso?: string | null, timeZone?: string | null): string | null {
  if (!iso) return null;
  const dateOnly = /^\d{4}-\d{2}-\d{2}/.exec(iso)?.[0];
  if (dateOnly) return formatDateOnly(dateOnly);

  return (
    formatDateTimeInTimeZone(iso, timeZone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) || null
  );
}

/**
 * NOT-339 §3.1 — human cadence summary for a recurring Job's config.
 * One shared formatter for the Job detail card and (later) the portal.
 * Degrades gracefully when the config is sparse. Dates render in the business
 * tz (pass the company timezone); never the browser's.
 */
export function recurrenceSummary(
  config?: RecurringConfig | null,
  timeZone?: string | null,
): string {
  if (!config || !config.frequency) return 'Recurring schedule';

  const interval = config.interval && config.interval > 1 ? config.interval : 1;

  // Base cadence phrase.
  let base: string;
  if (config.frequency === 'CUSTOM' && config.intervalUnit) {
    const unit = config.intervalUnit.toLowerCase();
    const singularUnit = unit.endsWith('s') ? unit.slice(0, -1) : unit;
    const pluralUnit = unit.endsWith('s') ? unit : `${unit}s`;
    base = interval === 1 ? `Every ${singularUnit}` : `Every ${interval} ${pluralUnit}`;
  } else {
    const noun = FREQUENCY_NOUN[config.frequency] ?? config.frequency.toLowerCase();
    base = interval === 1 ? `Every ${noun}` : `Every ${interval} ${noun}`;
  }

  // Weekly day-of-week pills.
  if (
    (config.frequency === 'WEEKLY' || config.frequency === 'BIWEEKLY') &&
    config.weekDays?.length
  ) {
    const days = config.weekDays
      .map((d) => WEEKDAY_LABELS[d])
      .filter(Boolean)
      .join(', ');
    if (days) base = `${base} on ${days}`;
  }

  // Monthly: nth weekday or day-of-month.
  if (config.frequency === 'MONTHLY') {
    if (
      config.monthMode === 'dayOfWeek' &&
      config.monthDayOfWeekOccurrence != null &&
      config.monthDayOfWeek != null
    ) {
      const ord =
        config.monthDayOfWeekOccurrence === -1
          ? 'last'
          : (ORDINALS[config.monthDayOfWeekOccurrence - 1] ?? '');
      const dow = WEEKDAY_LABELS[config.monthDayOfWeek];
      if (ord && dow) base = `${base} on the ${ord} ${dow}`;
    } else if (config.monthDay != null) {
      base = `${base} on day ${config.monthDay}`;
    }
  }

  // Term suffix.
  const start = fmtDate(config.startDate, timeZone);
  const parts = [base];
  if (start) parts.push(`started ${start}`);

  if (config.endType === 'ON_DATE') {
    const end = fmtDate(config.endOnDate, timeZone);
    if (end) parts.push(`until ${end}`);
  } else if (config.endType === 'AFTER_COUNT' && config.endAfterCount) {
    parts.push(`${config.endAfterCount} visits`);
  } else if (config.endType === 'INDEFINITE') {
    parts.push('evergreen');
  }

  return parts.join(' · ');
}
